// src/utils/datadogOutboundFunnel.ts
// 주문접수 퍼널(진입 → 요금·정산조건 확인 → 결제모달 진입 → 주문완료) 단계를 Datadog custom action으로 계측하기 위한 공통 헬퍼.
// 기존 컨벤션(datadogRum.addAction('<snake_case>', context))을 따르며, 단계별 액션명을 상수로 고정한다.
import { datadogRum } from '@datadog/browser-rum';
import type { OutboundServiceType } from './datadogOutboundFailure';

/** 대시보드 집계 단위 — 세부 service를 접수구분(퀵/택배/등기/국제우편)으로 묶는다. */
export type OutboundServiceGroup = 'quick' | 'parcel' | 'registry' | 'global';

/** 세부 service → 접수구분 그룹 매핑. 실패 계측(trackOutboundFailure)에서도 재사용한다. */
export const SERVICE_TO_GROUP: Record<OutboundServiceType, OutboundServiceGroup> = {
  quick: 'quick',
  freightQuick: 'quick',
  parcel: 'parcel',
  massParcel: 'parcel',
  registeredMail: 'registry',
  expressMail: 'registry',
  massRegistry: 'registry',
  dhl: 'global',
  ems: 'global',
  fedex: 'global',
  ups: 'global',
  globalMail: 'global',
};

/** 퍼널 단계 — 실제 액션명은 `outbound_<stage>` (대시보드 집계 기준 키) */
export type OutboundFunnelStage = 'enter' | 'rate_view' | 'payment_modal_open' | 'order_success';

export type OutboundFunnelContext = {
  /** 접수구분/서비스 (세부) */
  service?: OutboundServiceType;
  /** 접수구분 그룹. 생략 시 service로부터 자동 파생 */
  serviceGroup?: OutboundServiceGroup;
  /** 그 외 상황별 추가 태그 */
  [key: string]: unknown;
};

/**
 * `once: true`로 발사된 단계를 기억하는 키 집합.
 * 요금조회처럼 watch/debounce로 반복 호출되는 단계에서 중복 발사를 막는다.
 * 페이지 새로고침 시 초기화되므로 "세션당 1회"를 근사한다.
 */
const firedOnceKeys = new Set<string>();

/** service가 있으면 serviceGroup을 파생해 채운 컨텍스트를 돌려준다(명시값 우선). */
const withServiceGroup = (context: OutboundFunnelContext): OutboundFunnelContext => ({
  ...context,
  serviceGroup: context.serviceGroup ?? (context.service ? SERVICE_TO_GROUP[context.service] : undefined),
});

/**
 * 주문접수 이탈 추적기.
 * RUM은 세션 단위 차집합("A는 있고 B는 없는 세션") 검색이 안 되므로, 이탈을 액션으로 직접 남겨
 * 단일 필터로 세션 목록·리플레이를 볼 수 있게 한다.
 *
 * - outbound_stall   현재 단계에서 임계 시간이 지나도록 다음 단계로 못 넘어가면 발사
 * - outbound_abandon 접수 플로우 밖으로 벗어날 때 도달 단계와 함께 발사 (주문완료는 제외)
 *
 * 탭 닫기·모바일 앱 전환은 이탈 이벤트가 오지 않으므로, 단계가 바뀔 때마다 타이머를 다시 걸어
 * "요금조회 없이 멈춤"뿐 아니라 "요금은 봤는데 결제 안 하고 멈춤"까지 유실 없이 잡는다.
 */
export type OutboundAbandonStage = Extract<OutboundFunnelStage, 'enter' | 'rate_view' | 'payment_modal_open'>;

const STALL_THRESHOLDS_SEC: Record<OutboundAbandonStage, number[]> = {
  enter: [600],
  rate_view: [600],
  payment_modal_open: [600],
};

type AbandonWatch = {
  stage: OutboundAbandonStage;
  enteredAt: number;
  stageAt: number;
  baseContext: OutboundFunnelContext;
  getLiveContext: () => Record<string, unknown>;
  timers: ReturnType<typeof setTimeout>[];
};

let abandonWatch: AbandonWatch | null = null;

const elapsedSec = () => (abandonWatch ? Math.round((Date.now() - abandonWatch.enteredAt) / 1000) : 0);
const stageElapsedSec = () => (abandonWatch ? Math.round((Date.now() - abandonWatch.stageAt) / 1000) : 0);

// 언마운트 도중 호출될 수 있어 실패해도 추적을 막지 않는다.
const safeLiveContext = () => {
  try {
    return abandonWatch?.getLiveContext() ?? {};
  } catch {
    return {};
  }
};

const clearStallTimers = () => {
  if (!abandonWatch) return;
  abandonWatch.timers.forEach(clearTimeout);
  abandonWatch.timers = [];
};

// 현재 단계 기준으로 stall 타이머를 다시 건다.
const resetStallTimers = () => {
  if (!abandonWatch) return;
  clearStallTimers();
  const watch = abandonWatch;
  watch.timers = (STALL_THRESHOLDS_SEC[watch.stage] ?? []).map((thresholdSec) =>
    setTimeout(() => {
      datadogRum.addAction('outbound_stall', {
        reachedStage: watch.stage,
        thresholdSec,
        // 백그라운드 탭에서는 타이머가 지연될 수 있어 실제 경과 시간도 함께 남긴다
        stageSec: stageElapsedSec(),
        dwellSeconds: elapsedSec(),
        ...watch.baseContext,
        ...safeLiveContext(),
      });
    }, thresholdSec * 1000)
  );
};

/**
 * 접수 페이지 진입 시 이탈 감시를 시작한다.
 *
 * @param baseContext    세션 분류 정보(serviceGroup, 알면 service)
 * @param getLiveContext 발사 시점의 폼 입력 상태를 반환하는 함수(선택)
 * @param options        initialStage: 시작 단계(기본 enter)
 */
export const startOutboundAbandonWatch = (
  baseContext: OutboundFunnelContext = {},
  getLiveContext: () => Record<string, unknown> = () => ({}),
  options?: { initialStage?: OutboundAbandonStage }
) => {
  cancelOutboundAbandonWatch(); // 재진입(재주문/서비스 변경) 시 이전 감시 정리
  const now = Date.now();
  abandonWatch = {
    stage: options?.initialStage ?? 'enter',
    enteredAt: now,
    stageAt: now,
    baseContext: withServiceGroup(baseContext),
    getLiveContext,
    timers: [],
  };
  resetStallTimers();
};

/**
 * 도달 단계를 갱신하고, 새 단계 기준으로 stall 타이머를 다시 건다.
 * 진입 시점엔 serviceGroup만 알고 세부 service는 이후 단계에서 확정되므로 extra로 넘어오면 갱신한다.
 */
export const markOutboundStage = (stage: OutboundAbandonStage, extra?: OutboundFunnelContext) => {
  if (!abandonWatch) return;
  abandonWatch.stage = stage;
  abandonWatch.stageAt = Date.now();
  if (extra) {
    abandonWatch.baseContext = withServiceGroup({ ...abandonWatch.baseContext, ...extra });
  }
  resetStallTimers();
};

/** 이탈이 아닌 종료(주문완료)에서 감시를 해제한다. */
export const cancelOutboundAbandonWatch = () => {
  clearStallTimers();
  abandonWatch = null;
};

/** 접수 플로우를 떠날 때 도달 단계와 함께 이탈을 기록한다. 활성 감시가 없으면 no-op. */
export const flushOutboundAbandon = (exitType: string, extraContext?: Record<string, unknown>) => {
  if (!abandonWatch) return;
  const context = {
    reachedStage: abandonWatch.stage,
    exitType,
    dwellSeconds: elapsedSec(),
    stageSec: stageElapsedSec(),
    ...abandonWatch.baseContext,
    ...safeLiveContext(),
    ...extraContext,
  };
  cancelOutboundAbandonWatch(); // 중복 발사 방지
  datadogRum.addAction('outbound_abandon', context);
};

/**
 * 퍼널 단계 발사에 맞춰 이탈 감시 상태를 동기화한다.
 * trackOutboundFunnel의 once 디듑보다 위에서 호출되므로, 이벤트가 디듑돼도 단계 갱신은 매번 반영된다.
 */
const syncOutboundAbandonWatch = (stage: OutboundFunnelStage, context: OutboundFunnelContext) => {
  switch (stage) {
    case 'enter':
      startOutboundAbandonWatch(context);
      break;
    case 'rate_view':
    case 'payment_modal_open':
      // service가 없으면 extra를 넘기지 않는다 — 앞 단계에서 확정된 service를 undefined로 덮어쓰지 않도록
      markOutboundStage(stage, context.service ? { service: context.service } : undefined);
      break;
    case 'order_success':
      cancelOutboundAbandonWatch(); // 완료는 이탈이 아니다
      break;
  }
};

/**
 * 주문접수 퍼널 단계 이벤트를 Datadog custom action으로 전송한다.
 * @param stage   퍼널 단계. 액션명은 `outbound_<stage>`로 발사된다.
 * @param context service/serviceGroup 등 상황 태그. serviceGroup은 service로부터 자동 첨부된다.
 * @param options once=true면 같은 (stage, service) 조합을 페이지 로드당 1회만 발사한다.
 */
export const trackOutboundFunnel = (
  stage: OutboundFunnelStage,
  context: OutboundFunnelContext = {},
  options: { once?: boolean } = {}
) => {
  const enriched = withServiceGroup(context);

  // 이탈 감시 동기화는 once 디듑보다 먼저 — 이벤트가 디듑돼도 도달 단계는 매번 갱신되어야 한다
  syncOutboundAbandonWatch(stage, enriched);

  if (options.once) {
    const key = `${stage}:${context.service ?? enriched.serviceGroup ?? 'unknown'}`;
    if (firedOnceKeys.has(key)) return;
    firedOnceKeys.add(key);
  }

  datadogRum.addAction(`outbound_${stage}`, enriched);
};

/**
 * 로그아웃·세션 종료 시 once 상태 초기화 (다음 세션에서 퍼널 단계가 다시 잡히도록).
 * 남아있던 이탈 감시도 함께 정리해 로그아웃 후 stall이 늦게 발사되는 것을 막는다.
 */
export const resetOutboundFunnelOnceState = () => {
  firedOnceKeys.clear();
  cancelOutboundAbandonWatch();
};
